import { useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import MediaCard from "@/components/base/MediaCard";
import Toast from "@/components/base/Toast";
import { useFavorites } from "@/hooks/useFavorites";
import { usePageMeta } from "@/hooks/usePageMeta";
import { mediaItems } from "@/mocks/media";
import { newsItems } from "@/mocks/news";
import type { MediaItem } from "@/types/content";

type MediaDetailProps = {
  item: MediaItem;
};

export default function MediaDetail({ item }: MediaDetailProps) {
  const { isFavorite, toggleFavorite } = useFavorites();
  const [toast, setToast] = useState("");
  const navigate = useNavigate();
  const favorited = isFavorite(item.id);

  usePageMeta(
    `${item.title} - 详情简介 - 天马传媒`,
    `${item.title}（${item.year}）${item.genres.join("、")}题材${item.type === "movie" ? "电影" : "剧集"}，导演${item.director}，${item.cast.slice(0, 3).join("、")}主演。${item.summary}`,
    `${item.title},${item.genres.join(",")},天马传媒,影视详情`,
  );

  const related = mediaItems
    .filter(
      (candidate) =>
        candidate.id !== item.id &&
        ((candidate.type === item.type &&
          candidate.genres.some((genre) => item.genres.includes(genre))) ||
          candidate.director === item.director),
    )
    .slice(0, 4);

  const fallbackRelated =
    related.length > 0
      ? related
      : mediaItems.filter((candidate) => candidate.id !== item.id).slice(0, 4);

  const relatedNews = [
    ...newsItems.filter((news) => news.relatedMediaId === item.id),
    ...newsItems.filter((news) => news.relatedMediaId !== item.id),
  ].slice(0, 3);

  const handleShare = async () => {
    const url = window.location.href;
    try {
      await navigator.clipboard.writeText(url);
      setToast("链接已复制，可以分享给朋友了");
    } catch {
      setToast("复制失败，请手动复制浏览器地址");
    }
  };

  const handleFavorite = () => {
    const next = toggleFavorite(item.id);
    setToast(next ? "已加入我的收藏（保存在本机浏览器）" : "已取消收藏");
  };

  const infoRows = [
    { label: "导演", value: item.director },
    { label: "主演", value: item.cast.join(" / ") },
    { label: "类型", value: item.genres.join(" / ") },
    { label: "地区", value: item.region },
    { label: "年份", value: String(item.year) },
    { label: item.type === "movie" ? "片长" : "单集时长", value: item.duration },
  ];

  return (
    <div className="min-h-screen bg-background-50">
      <div className="border-b border-background-200 bg-background-100">
        <div className="shell py-6 md:py-8">
          <nav className="flex flex-wrap items-center gap-1.5 text-xs text-foreground-500" aria-label="面包屑">
            <Link to="/" className="cursor-pointer transition hover:text-primary-600">
              首页
            </Link>
            <i className="ri-arrow-right-s-line text-foreground-300"></i>
            <Link
              to={`/category/${item.type}`}
              className="cursor-pointer transition hover:text-primary-600"
            >
              {item.type === "movie" ? "电影" : "电视剧"}
            </Link>
            <i className="ri-arrow-right-s-line text-foreground-300"></i>
            <span className="text-foreground-800">{item.title}</span>
          </nav>

          <div className="mt-4 flex flex-wrap items-center gap-2 text-xs">
            <span className="rounded border border-primary-100 bg-primary-50 px-2 py-1 text-primary-700">
              {item.type === "movie" ? "电影" : "剧集"}
            </span>
            {item.genres.map((genre) => (
              <Link
                key={genre}
                to={`/category/${item.type}?genre=${encodeURIComponent(genre)}`}
                className="rounded border border-background-200 bg-background-50 px-2 py-1 text-foreground-600 transition hover:border-primary-300 hover:text-primary-600"
              >
                {genre}
              </Link>
            ))}
            <span className="text-foreground-500">
              {item.year} · {item.region} · {item.episodes ?? item.duration}
            </span>
          </div>

          <h1 className="mt-3 font-heading text-2xl font-bold text-foreground-950 md:text-3xl">
            {item.title}
          </h1>
        </div>
      </div>

      <main className="shell py-8 md:py-10">
        <div className="grid gap-8 lg:grid-cols-[300px_1fr] lg:gap-10">
          <div className="mx-auto w-full max-w-[300px] lg:sticky lg:top-24 lg:max-w-none lg:self-start">
            <div className="relative overflow-hidden rounded-lg border border-background-200 bg-primary-900">
              <div className="aspect-[2/3] w-full">
                <img
                  src={item.poster}
                  alt={`${item.title}主海报`}
                  title={`${item.title} - 天马传媒作品详情`}
                  className="h-full w-full object-cover object-top"
                />
              </div>
              <div className="absolute right-3 top-3 flex items-center gap-1 rounded-md bg-primary-950/75 px-2 py-1 text-sm font-medium text-accent-300 backdrop-blur">
                <i className="ri-star-fill text-xs"></i>
                {item.rating.toFixed(1)}
              </div>
            </div>

            <div className="mt-3 grid grid-cols-2 gap-2">
              <button
                type="button"
                onClick={handleFavorite}
                className={`flex cursor-pointer items-center justify-center gap-1.5 whitespace-nowrap rounded-md border px-3 py-2.5 text-sm transition ${
                  favorited
                    ? "border-accent-500 bg-accent-500 text-primary-950"
                    : "border-background-300 bg-background-50 text-foreground-700 hover:border-accent-400 hover:text-accent-700"
                }`}
              >
                <i className={favorited ? "ri-heart-fill" : "ri-heart-line"}></i>
                {favorited ? "已收藏" : "收藏"}
              </button>
              <button
                type="button"
                onClick={handleShare}
                className="flex cursor-pointer items-center justify-center gap-1.5 whitespace-nowrap rounded-md border border-background-300 bg-background-50 px-3 py-2.5 text-sm text-foreground-700 transition hover:border-primary-300 hover:text-primary-600"
              >
                <i className="ri-share-forward-line"></i>
                分享
              </button>
            </div>

            <div className="mt-4 rounded-lg border border-background-200 bg-background-50 p-4">
              <dl className="space-y-2.5 text-xs">
                {infoRows.map((row) => (
                  <div key={row.label} className="flex gap-3">
                    <dt className="w-12 shrink-0 text-foreground-500">{row.label}</dt>
                    <dd className="min-w-0 flex-1 text-foreground-800">{row.value}</dd>
                  </div>
                ))}
              </dl>
            </div>

            <div className="mt-4 flex flex-wrap items-center gap-1.5">
              <span className="mr-1 text-xs text-foreground-500">所属分类</span>
              <Link
                to={`/category/${item.type}`}
                className="cursor-pointer rounded border border-primary-100 bg-primary-50 px-2 py-1 text-xs text-primary-700 transition hover:border-primary-300"
              >
                {item.type === "movie" ? "电影" : "电视剧"}
              </Link>
              <Link
                to={`/category/${item.type}?genre=${encodeURIComponent(item.genres[0])}`}
                className="cursor-pointer rounded border border-background-200 bg-background-100 px-2 py-1 text-xs text-foreground-600 transition hover:border-primary-300 hover:text-primary-600"
              >
                {item.genres[0]}
              </Link>
            </div>

            <div className="mt-3 flex flex-wrap gap-2 text-xs">
              <Link
                to={`/category/${item.type}`}
                className="inline-flex cursor-pointer items-center gap-1 rounded-md bg-background-100 px-3 py-2 text-foreground-700 transition hover:bg-primary-50 hover:text-primary-600"
              >
                <i className="ri-arrow-left-line"></i>
                返回{item.type === "movie" ? "电影" : "剧集"}列表
              </Link>
              <Link
                to="/specials"
                className="inline-flex cursor-pointer items-center gap-1 rounded-md bg-background-100 px-3 py-2 text-foreground-700 transition hover:bg-primary-50 hover:text-primary-600"
              >
                <i className="ri-stack-line"></i>
                相关专题
              </Link>
            </div>
          </div>

          <div className="min-w-0">
            <p className="border-l-2 border-accent-500 pl-4 font-heading text-base leading-relaxed text-foreground-800 md:text-lg">
              {item.tagline}
            </p>

            <section className="mt-8">
              <h2 className="flex items-center gap-2 font-heading text-lg font-semibold text-foreground-950">
                <span className="h-4 w-1 rounded-full bg-primary-500"></span>
                剧情简介
              </h2>
              <p className="mt-4 rounded-md bg-background-100 p-4 text-sm leading-relaxed text-foreground-700">
                {item.summary}
              </p>
              <div className="mt-4 space-y-3 text-sm leading-7 text-foreground-700">
                {item.synopsis.map((paragraph) => (
                  <p key={paragraph.slice(0, 16)}>{paragraph}</p>
                ))}
              </div>
            </section>

            <section className="mt-9">
              <div className="flex items-center justify-between border-b border-background-200 pb-3">
                <h2 className="flex items-center gap-2 font-heading text-lg font-semibold text-foreground-950">
                  <span className="h-4 w-1 rounded-full bg-primary-500"></span>
                  相关影视资讯
                </h2>
                <Link
                  to="/category/news"
                  className="cursor-pointer text-xs text-primary-600 transition hover:text-accent-600"
                >
                  更多资讯
                </Link>
              </div>
              <ul className="divide-y divide-background-200">
                {relatedNews.map((news) => (
                  <li key={news.id}>
                    <Link
                      to={`/detail/${news.id}`}
                      className="group flex cursor-pointer items-center gap-4 py-3.5"
                    >
                      <span className="rounded border border-accent-200 bg-accent-50 px-1.5 py-0.5 text-[10px] text-accent-800">
                        {news.category}
                      </span>
                      <span className="min-w-0 flex-1">
                        <span className="line-clamp-1 text-sm text-foreground-900 transition group-hover:text-primary-600">
                          {news.title}
                        </span>
                        <span className="mt-0.5 block text-[11px] text-foreground-500">
                          {news.date}
                        </span>
                      </span>
                      <i className="ri-arrow-right-line text-foreground-300 transition group-hover:translate-x-1 group-hover:text-accent-600"></i>
                    </Link>
                  </li>
                ))}
              </ul>
            </section>

            <section className="mt-9">
              <div className="flex items-center justify-between border-b border-background-200 pb-3">
                <h2 className="flex items-center gap-2 font-heading text-lg font-semibold text-foreground-950">
                  <span className="h-4 w-1 rounded-full bg-primary-500"></span>
                  你可能还喜欢
                </h2>
                <button
                  type="button"
                  onClick={() => navigate(`/category/${item.type}`)}
                  className="cursor-pointer whitespace-nowrap text-xs text-primary-600 transition hover:text-accent-600"
                >
                  更多同类型
                </button>
              </div>
              <div className="mt-5 grid grid-cols-2 gap-4 sm:grid-cols-4">
                {fallbackRelated.map((candidate) => (
                  <MediaCard key={candidate.id} item={candidate} />
                ))}
              </div>
            </section>
          </div>
        </div>
      </main>

      <Toast message={toast} onClose={() => setToast("")} />
    </div>
  );
}